/**
 * ProtectedRoute - Route guard component
 * 
 * Protects routes based on user roles and permissions
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import LoadingScreen from './LoadingScreen';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredPermission = null,
  fallbackRoute = '/',
  loadingMessage = "Checking permissions..."
}) => {
  const { 
    profile, 
    isLoading, 
    error, 
    isUnprofiled,
    hasRole,
    canAccessAdmin,
    canAccessProjectSettings 
  } = usePermissions();

  // Show loading while checking permissions OR if profile is not yet loaded
  const profileNotLoaded = profile.globalRoles === undefined && profile.roles && profile.roles.length === 0;
  if (isLoading || profileNotLoaded) {
    return <LoadingScreen message={loadingMessage} />;
  }

  // Handle errors
  if (error) {
    return <Navigate to="/error" replace />;
  }

  // Handle unprofiled users
  if (isUnprofiled) {
    return <Navigate to="/awaiting-approval" replace />;
  }

  // Check role-based access
  if (requiredRole) {
    const hasRequiredRole = hasRole(requiredRole);
    
    if (!hasRequiredRole) {
      console.warn(`Access denied: User lacks required role '${requiredRole}'`);
      return <Navigate to={fallbackRoute} replace />;
    }
  }

  // Check permission-based access
  if (requiredPermission) {
    let hasPermission = false;
    
    switch (requiredPermission) {
      case 'admin':
        hasPermission = canAccessAdmin();
        break;
      case 'project_settings':
        hasPermission = canAccessProjectSettings();
        break;
      default:
        console.warn(`Unknown permission check: ${requiredPermission}`);
        hasPermission = false;
    }
    
    if (!hasPermission) {
      console.warn(`Access denied: User lacks required permission '${requiredPermission}'`);
      return <Navigate to={fallbackRoute} replace />;
    }
  }

  // Access granted
  return children;
};

/**
 * AdminRoute - Shorthand for admin-only routes
 */
export const AdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute 
      requiredPermission="admin"
      fallbackRoute="/"
      loadingMessage="Verifying admin access..."
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * ProjectAdminRoute - Shorthand for project admin routes
 */
export const ProjectAdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute 
      requiredPermission="project_settings"
      fallbackRoute="/"
      loadingMessage="Verifying project access..."
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

export default ProtectedRoute; 